import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Receipt } from 'lucide-react';

const TYPE_COLORS = {
  setup_fee: 'text-amber-400',
  wallet_topup: 'text-blue-400',
  profit_deduction: 'text-red-400',
  referral_commission: 'text-primary-400',
  profit_commission: 'text-green-400',
  achievement_bonus: 'text-pink-400',
  withdrawal: 'text-purple-400',
  withdrawal_rejected: 'text-gray-400',
};

export default function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    const params = new URLSearchParams({ page, limit: 25 });
    if (typeFilter) params.append('type', typeFilter);
    axios.get(`/api/admin/transactions?${params}`).then(r => {
      setTransactions(r.data.transactions);
      setTotal(r.data.total);
    });
  }, [page, typeFilter]);

  const txTypes = ['setup_fee', 'wallet_topup', 'profit_deduction', 'referral_commission', 'profit_commission', 'achievement_bonus', 'withdrawal'];

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">All Transactions</h1>
          <p className="text-gray-400 mt-1">Complete transaction ledger</p>
        </div>

        {/* Type Filter */}
        <div className="flex gap-2 flex-wrap mb-6">
          <button onClick={() => { setTypeFilter(''); setPage(1); }}
            className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${!typeFilter ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5 hover:text-white'}`}>
            All
          </button>
          {txTypes.map(t => (
            <button key={t} onClick={() => { setTypeFilter(t); setPage(1); }}
              className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-colors capitalize ${typeFilter === t ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5 hover:text-white'}`}>
              {t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">User</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Type</th>
                  <th className="text-right px-4 py-3 text-gray-400 text-sm font-medium">Amount</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Wallet</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Description</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-500">No transaction history. found</td></tr>
                ) : transactions.map(t => (
                  <tr key={t._id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-medium">{t.user?.name}</p>
                      <p className="text-gray-500 text-xs">{t.user?.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium capitalize ${TYPE_COLORS[t.type] || 'text-gray-400'}`}>
                        {t.type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold text-sm ${['referral_commission','profit_commission','achievement_bonus','wallet_topup'].includes(t.type) ? 'text-primary-400' : 'text-red-400'}`}>
                        ${t.amount.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm capitalize">{t.walletType}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs max-w-xs truncate">{t.description || '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-sm">{new Date(t.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 25 && (
            <div className="p-4 border-t border-white/5 flex items-center justify-between">
              <p className="text-gray-400 text-sm">Total: {total}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                  className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Prev</button>
                <button onClick={() => setPage(p => p+1)} disabled={page*25>=total}
                  className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
